import { Injectable } from '@angular/core';

import { IsoDate } from '../../shared/utils/iso-date';
import {
  DailyChallengeCacheEntry,
  DailyChallengeSessionStatus,
} from '../models/cache/daily-challenge-cache-entry';

const KEY_PREFIX = 'quem-sou-eu:daily:';

function keyOf(date: IsoDate, idTheme: string): string {
  return `${KEY_PREFIX}${date}:${idTheme}`;
}

/**
 * Partidas do Desafio Diário no localStorage (`quem-sou-eu:daily:{yyyy-MM-dd}:{idTheme}`).
 * É o front quem garante "uma partida por tema/dia" — o backend não bloqueia.
 *
 * Sem TTL (diferente de `CacheEntry`): a entrada vale para sempre. Todo acesso ao
 * Storage é protegido por try/catch (modo anônimo / armazenamento bloqueado); sem
 * cache, o jogador simplesmente começa uma sessão nova.
 */
@Injectable({ providedIn: 'root' })
export class DailyChallengeCacheService {
  get(date: IsoDate, idTheme: string): DailyChallengeCacheEntry | null {
    try {
      return this.parse(localStorage.getItem(keyOf(date, idTheme)));
    } catch {
      return null;
    }
  }

  save(idSession: string, idTheme: string, date: IsoDate, sessionStatus: DailyChallengeSessionStatus): void {
    const entry: DailyChallengeCacheEntry = {
      idSession,
      idTheme,
      challengeDate: date,
      sessionStatus,
      updatedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(keyOf(date, idTheme), JSON.stringify(entry));
    } catch (error) {
      console.warn('Não foi possível salvar o Desafio Diário no cache.', error);
    }
  }

  remove(date: IsoDate, idTheme: string): void {
    try {
      localStorage.removeItem(keyOf(date, idTheme));
    } catch {
      // Sem acesso ao Storage não há o que remover.
    }
  }

  /** Status de cada dia já jogado (ou em andamento) no tema, indexado pela data. */
  statusByDate(idTheme: string): Map<IsoDate, DailyChallengeSessionStatus> {
    const statuses = new Map<IsoDate, DailyChallengeSessionStatus>();
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(KEY_PREFIX) || !key.endsWith(`:${idTheme}`)) {
          continue;
        }
        const entry = this.parse(localStorage.getItem(key));
        if (entry) {
          statuses.set(entry.challengeDate, entry.sessionStatus);
        }
      }
    } catch {
      // Sem acesso ao Storage: nenhum dia marcado.
    }
    return statuses;
  }

  private parse(raw: string | null): DailyChallengeCacheEntry | null {
    try {
      return raw ? (JSON.parse(raw) as DailyChallengeCacheEntry) : null;
    } catch {
      return null;
    }
  }
}
